import React from 'react'
import { Helmet } from 'react-helmet'
import ReactPlayer from 'react-player'
import { IonGrid, IonRow, IonHeader, IonToolbar, IonButtons, IonCol } from '@ionic/react'
import { isIOSDevice } from 'mobile_web/modules/platform/device'
import Slider from '../../../common/components/AccessibleSlider'

import {
	ScreenSectionItem,
	SectionMediaVideoContainer as Container,
	SCREEN_SECTION_TYPE_WEB_VIDEO,
	formatTimeMS,
} from 'mytours-read-common'

import TranscriptModalCard from '../../../audio/components/TranscriptModalCard'
import { useTimecodedTrackUrl } from '../../../audio/useTimecodedTrackUrl'
import { isRTL, isMobileOrTabletDevice, getCurrentLanguage } from '../../../platform'

import Loader from '../../../common/components/Loader'
import { Button } from '@mytours/components'
import Modal from '../../../common/components/modal'
import classNames from 'classnames'

import strings from '../../../../strings'

import '../styles/fullScreenVideo.less'
import { ImageSizePreset } from '../../../../common_lib/types'
import { generateImageUrl, generateVideoUrl } from '../../../../common_lib/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { updateTranscriptionHash } from '../../functions'
import {
	applyCaptionMode,
	getVideoUrl,
	isRemoteVideo,
	ReactPlayerConfig,
	sendVideoPauseEvent,
	sendVideoPlayEvent,
	sendVideoProgressEvent,
} from './functions'

interface OwnProps {
	sectionItem: ScreenSectionItem
	sectionId: number
	sectionTitle: string
	containerName: string
	onBack: () => void
	setFullScreenModal: (status: boolean) => void
	isFullScreenModal?: boolean
	isSingleItem?: boolean
	isOpen: boolean
	captionTrackUrl?: string
}

interface State {
	isPaused: boolean
	showControls: boolean
	progress: number
	duration?: number
	userDraggingSlider: boolean
	showTranscriptModal: boolean
	isReady: boolean
	captionsOn: boolean
}

type Props = OwnProps & Container.Props & Container.Actions

interface DocElementFullScreen {
	mozRequestFullScreen(): Promise<void>
	webkitRequestFullscreen(): Promise<void>
	msRequestFullscreen(): Promise<void>
}

interface DocElementExitFullScreen {
	mozCancelFullScreen(): Promise<void>
	webkitExitFullscreen(): Promise<void>
	msExitFullscreen(): Promise<void>
}

/**
 * The initial state for our internal component state.
 */
const INITIAL_STATE: State = {
	isPaused: true,
	showControls: true,
	userDraggingSlider: false,
	progress: 0,
	showTranscriptModal: false,
	isReady: false,
	captionsOn: false,
}

interface VideoProgressData {
	played: number
	playedSeconds: number
	loaded: number
	loadedSeconds: number
}

interface WebKitDocument {
	fullscreenElement: any
	mozFullScreenElement: any
	webkitFullscreenElement: any
}

export class FullScreenVideo extends React.Component<Props, State> {
	state = INITIAL_STATE

	player: ReactPlayer | null
	isVideoStarted = false
	private captionTrackEl: HTMLTrackElement | null = null
	private captionTrackUrlApplied: string | null = null

	private hash = ''
	private unListen: () => void
	private progressBreakpoints = {
		10: false,
		25: false,
		50: false,
		75: false,
	}

	componentDidMount() {
		const {
			sectionItem: { file },
			videoFilePath,
		} = this.props

		if (!videoFilePath && file) {
			const url = generateVideoUrl(file, {
				pathType: file.dont_transform ? 'original' : 'video_generic480p',
			})
			this.props.loadVideoPath(file.filename, url)
		}

		addEventListener('fullscreenchange', this.fullscreenChange)

		this.unListen = ionHistory.listen(({ hash }) => {
			if (hash !== this.hash) {
				// TODO: this is temp fix: add global handle for hash changes
				if (hash === '' && this.hash === '#transcript' && this.state.showTranscriptModal) {
					this.toggleTranscriptModal(false)
				}

				this.hash = hash
			}
		})
	}

	componentWillUnmount(): void {
		removeEventListener('fullscreenchange', this.fullscreenChange)

		this.unListen()
	}

	componentDidUpdate(): void {
		const document: any = window.document
		const doc: Document & WebKitDocument = document

		const fullscreenElement = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement

		if (Boolean(fullscreenElement) !== this.props.isFullScreenModal) {
			this.props.setFullScreenModal(Boolean(fullscreenElement))
		}

		// Catch the case where the caption-track URL resolves after the player is ready.
		this.syncCaptions()
	}

	fullscreenChange = (): void => {
		const document: any = window.document
		const doc: Document & WebKitDocument = document

		const fullscreenElement = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement
		// Close video modal if it is only media item in section and native player is used
		if (!fullscreenElement && this.props.isSingleItem && isMobileOrTabletDevice()) {
			this.props.onBack()
		}
		this.forceUpdate()
	}

	get isLoadingVideoPath(): boolean {
		const {
			videoFilePath,
			loadingVideoPath,
			sectionItem: { media_type: mediaType },
		} = this.props
		if (mediaType === SCREEN_SECTION_TYPE_WEB_VIDEO) {
			return false
		}

		return loadingVideoPath || videoFilePath === undefined || videoFilePath === null
	}

	get playPauseButtonIcon(): string {
		return this.state.isPaused ? 'play' : 'pause'
	}

	onPlayPausePress = () => {
		this.setState((prev: State) => ({ isPaused: !prev.isPaused }))
	}

	onSliderValueChanged = (value: number) => {
		// update user dragging state (but only if we need to as this callback gets hit a lot while dragging the slider)
		if (!this.state.userDraggingSlider) {
			this.setState({ userDraggingSlider: true })
		}

		if (value === 100) {
			this.player.seekTo(0)
			this.setState({ isPaused: true })
		}

		this.setState({ progress: value })
	}

	onSlidingComplete = () => {
		const { progress } = this.state
		const value = Math.round(progress)
		const { duration } = this.state

		if (this.player && duration) {
			const newTime = duration * (value / 100)

			if (newTime === duration) {
				this.player.seekTo(0)
				this.setState({ isPaused: true })
			} else {
				this.player.seekTo(newTime)
			}
		}
		// note we set the new current time on state immediately for cases like when the user was dragging slider but audio completed before user let go of slider.
		// in that scenario the slider and time text updates to correct position but audio does not play until user presses Play button again
		this.setState({ userDraggingSlider: false })
	}

	onProgress = (data: VideoProgressData) => {
		const { userDraggingSlider, progress } = this.state
		const videoDuration = this.player.getDuration() || 1
		const newProgress = userDraggingSlider ? progress : (data.playedSeconds / videoDuration) * 100

		sendVideoProgressEvent(this.player, this.videoEventParams, data.played, this.progressBreakpoints)

		if (newProgress === 100) {
			this.player.seekTo(0)
			this.setState({ isPaused: true })
		}
		this.setState({ duration: videoDuration, progress: newProgress })
	}

	toggleCaptions = (): void => {
		this.setState(prev => ({ captionsOn: !prev.captionsOn }), this.syncCaptions)
	}

	// react-player memoizes its config on first render, so an async caption-track URL
	// never reaches the <video> via config.file.tracks. Instead we inject the <track>
	// onto the underlying media element ourselves, once the player is ready and the URL
	// has resolved (which can happen in either order), then drive its visibility.
	private ensureCaptionTrackEl = (): void => {
		const { captionTrackUrl } = this.props
		const video = this.player?.getInternalPlayer() as HTMLVideoElement | undefined

		// The track URL resolved away (e.g. language switched to a media item with
		// no timecoded transcription). Drop the now-stale track — its blob URL has
		// already been revoked — instead of leaving it on the <video>.
		if (!captionTrackUrl) {
			this.captionTrackEl?.remove()
			this.captionTrackEl = null
			this.captionTrackUrlApplied = null
			return
		}
		if (!video || typeof video.appendChild !== 'function') return
		if (this.captionTrackUrlApplied === captionTrackUrl) return

		this.captionTrackEl?.remove()
		const lang = getCurrentLanguage()
		const track = document.createElement('track')
		track.kind = 'captions'
		track.label = lang
		track.srclang = lang
		track.default = false
		track.src = captionTrackUrl
		video.appendChild(track)
		this.captionTrackEl = track
		this.captionTrackUrlApplied = captionTrackUrl
	}

	private syncCaptions = (): void => {
		this.ensureCaptionTrackEl()
		applyCaptionMode(this.player, this.state.captionsOn)
	}

	onTranscriptPressed = () => {
		const { sectionItem } = this.props
		if (sectionItem.transcription) {
			this.toggleTranscriptModal(true)
		}
	}

	toggleTranscriptModal = (value?: boolean): void => {
		this.setState((prev: State) => {
			updateTranscriptionHash(value, prev.showTranscriptModal, () => {
				// TODO: add `video_view_transcript` event
			})

			return {
				showTranscriptModal: typeof value === 'boolean' ? value : !prev.showTranscriptModal,
			}
		})
	}

	renderTranscriptModal = (): JSX.Element => {
		const { sectionItem } = this.props
		const { showTranscriptModal } = this.state

		return (
			<Modal isOpen={showTranscriptModal} onDidDismiss={() => this.toggleTranscriptModal(false)}>
				<TranscriptModalCard
					onClose={() => this.toggleTranscriptModal(false)}
					transcription={sectionItem.transcription}
					transcriptionFormat={sectionItem.transcription_format}
					mediaItemId={sectionItem.id}
					currentTime={this.player?.getCurrentTime() ?? 0}
					isPlaying={!this.state.isPaused}
				/>
			</Modal>
		)
	}

	private toggleControls = () => {
		this.setState((prev: State) => ({ showControls: !prev.showControls }))
	}

	handleControls = e => {
		const {
			target: { attributes, parentElement },
		} = e
		const hasAttribute: boolean = attributes?.['player-action-attribute']
		const parentHasAttribute: boolean = parentElement?.attributes?.['player-action-attribute']

		if (hasAttribute || parentHasAttribute) {
			this.setState({ showControls: true })
		} else {
			this.toggleControls()
		}
	}

	private requestFullScreen = () => {
		const { setFullScreenModal } = this.props
		const element = window.document.documentElement as HTMLElement & DocElementFullScreen

		if (element.requestFullscreen || element.webkitRequestFullscreen || element.mozRequestFullScreen) {
			if (setFullScreenModal) setFullScreenModal(true)
		}

		if (element.requestFullscreen) {
			element.requestFullscreen()
		} else if (element.webkitRequestFullscreen) {
			element.webkitRequestFullscreen()
		} else if (element.mozRequestFullScreen) {
			element.mozRequestFullScreen()
		}
	}

	private fullScreenCancel = () => {
		const { setFullScreenModal } = this.props
		const doc = window.document as Document & DocElementExitFullScreen

		if (doc.exitFullscreen || doc.mozCancelFullScreen || doc.msExitFullscreen || doc.webkitExitFullscreen) {
			if (setFullScreenModal) setFullScreenModal(false)
		}

		if (doc.exitFullscreen) {
			doc.exitFullscreen()
		} else if (doc.mozCancelFullScreen) {
			/* Firefox */
			doc.mozCancelFullScreen()
		} else if (doc.webkitExitFullscreen) {
			/* Chrome, Safari and Opera */
			doc.webkitExitFullscreen()
		} else if (doc.msExitFullscreen) {
			/* IE/Edge */
			doc.msExitFullscreen()
		}
	}

	onFullScreenToggle = (): void => {
		const { isFullScreenModal } = this.props
		if (isFullScreenModal) {
			this.fullScreenCancel()
		} else {
			this.requestFullScreen()
		}
	}

	onClose = (): void => {
		const { setFullScreenModal, onBack } = this.props
		if (setFullScreenModal) setFullScreenModal(false)
		if (onBack) onBack()
	}

	onReady = (player: ReactPlayer): void => {
		if (this.state.isReady) {
			return
		}

		this.player = player
		this.setState({ isReady: true, isPaused: !this.props.isOpen })
		this.syncCaptions()

		setTimeout(() => {
			if (!this.state.isPaused) {
				this.setState({ showControls: false })
			}
		}, 2000)
	}

	onError = (): void => {
		// ipad ios doesn't support autoplay, we pause video and then user should tap on play button
		// react-player version 2.7.0
		this.setState({ isReady: true, isPaused: true })
	}

	get videoEventParams() {
		const { sectionItem, sectionId, sectionTitle, containerName } = this.props

		return {
			sectionItemName: sectionItem.name,
			sectionId,
			sectionTitle,
			containerName,
		}
	}

	onPlay = () => {
		sendVideoPlayEvent(this.player, this.videoEventParams, this.isVideoStarted)
		this.isVideoStarted = true
	}

	onPause = () => {
		sendVideoPauseEvent(this.player, this.videoEventParams)
	}

	onVideoEnd = () => {
		this.setState({ isPaused: true }, () => {
			this.player.seekTo(0)
		})

		this.isVideoStarted = false
		this.progressBreakpoints = {
			10: false,
			25: false,
			50: false,
			75: false,
		}
	}

	render() {
		const {
			sectionItem: { thumbnail_media_item: thumbnailMediaItem, transcription, title, remote_type: remoteType },
			isFullScreenModal,
			videoFilePath,
		} = this.props
		const { isPaused, showControls, progress, isReady, duration } = this.state

		const isLoading = this.isLoadingVideoPath || !isReady
		const hasTranscript = !!transcription

		const imageUrl: string =
			thumbnailMediaItem?.file &&
			generateImageUrl(thumbnailMediaItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			})

		const filename = thumbnailMediaItem?.file?.filename || title || ''

		const schema = {
			type: 'application/ld+json',
			innerHTML: JSON.stringify({
				'@type': 'VideoObject',
				name: filename,
				description: filename,
				thumbnailUrl: imageUrl,
			}),
		}
		const url = getVideoUrl(videoFilePath, this.props.sectionItem)
		const isEmbed = isRemoteVideo(remoteType)
		const showVideoControls = showControls && isReady && !isEmbed

		const { captionTrackUrl } = this.props
		const showCaptionsButton = showVideoControls && !!captionTrackUrl

		const currentTime = this.player?.getCurrentTime()

		const elapsedTime = currentTime ? Math.round(currentTime) : 0
		const formattedDuration = duration ? Math.round(duration) : 0
		const formattedElapsedTime = formatTimeMS(elapsedTime)
		const formattedDurationTime = formatTimeMS(formattedDuration)

		return (
			<>
				<Helmet script={[schema]} />
				<IonGrid
					className={classNames('fullscreen-video-container', {
						embed: isEmbed,
					})}
					data-cy="fullscreen-video-container"
				>
					<IonRow className="fullscreen-video-row" onClick={this.handleControls}>
						<IonCol>
							<IonRow className="header-row">
								<IonHeader className="header fullscreen-video-header">
									<IonToolbar className="header-toolbar">
										{(showControls || isEmbed) && (
											<IonButtons slot="end" className="header-buttons">
												<Button
													variant="clear"
													className="back-button"
													onClick={this.onClose}
													player-action-attribute="player-action-attribute"
													aria-label={strings.accessibility_go_back}
												>
													<i className="fal fa-xmark close-icon" />
												</Button>
											</IonButtons>
										)}
									</IonToolbar>
								</IonHeader>
							</IonRow>

							{isLoading && (
								<IonRow className="loading-row">
									<IonRow>
										<Loader />
									</IonRow>
								</IonRow>
							)}

							<IonRow className="player-row">
								<IonRow className="player-row-inner">
									<ReactPlayer
										url={url}
										playing={!isPaused}
										onProgress={this.onProgress}
										width="100%"
										height="100%"
										onReady={this.onReady}
										onError={this.onError}
										onPlay={this.onPlay}
										onPause={this.onPause}
										onEnded={this.onVideoEnd}
										muted={false}
										config={ReactPlayerConfig}
									/>
									{showVideoControls && (
										<div className="controls-wrapper">
											<div className="controls">
												<Button
													className="control-button"
													variant="clear"
													onClick={this.onPlayPausePress}
													aria-label={isPaused ? strings.play_media : strings.pause_media}
												>
													<i
														className={`fas fa-${this.playPauseButtonIcon}`}
														player-action-attribute="player-action-attribute"
													/>
												</Button>
											</div>
										</div>
									)}
								</IonRow>
							</IonRow>
							{showVideoControls && (
								<IonRow
									className="video-range-row"
									style={{ paddingBottom: isIOSDevice() ? 10 : 20 }}
									player-action-attribute="player-action-attribute"
								>
									<IonCol>
										<IonCol className="duration-col">{formattedElapsedTime} </IonCol>
										<Slider
											className={`slider-range ${isIOSDevice() && 'slider-range-ios'}`}
											ariaLabel={strings.accessibility_video_seek}
											max={100}
											min={0}
											value={progress}
											tooltip={false}
											onChange={this.onSliderValueChanged}
											onChangeComplete={this.onSlidingComplete}
											reverse={isRTL()}
											player-action-attribute="player-action-attribute"
										/>
										<IonCol className="duration-col">{formattedDurationTime}</IonCol>
									</IonCol>
									{showCaptionsButton && (
										<IonRow className="video-cc-row">
											<Button
												variant="clear"
												className={classNames('transcript-button', 'cc-button', { active: this.state.captionsOn })}
												aria-label={strings.captions}
												aria-pressed={this.state.captionsOn}
												onClick={this.toggleCaptions}
											>
												<i className="fas fa-closed-captioning" player-action-attribute="player-action-attribute" />
											</Button>
										</IonRow>
									)}
									{hasTranscript && (
										<IonRow className="video-transcript-row">
											<Button
												variant="clear"
												className="transcript-button"
												aria-label={strings.transcript}
												onClick={this.onTranscriptPressed}
											>
												<i className="fal fa-align-left" player-action-attribute="player-action-attribute" />
											</Button>
										</IonRow>
									)}
									<IonRow className="expand-row">
										<Button
											variant="clear"
											className="transcript-button"
											onClick={this.onFullScreenToggle}
											aria-label={
												isFullScreenModal
													? strings.accessibility_close_fullscreen_player
													: strings.accessibility_open_fullscreen_player
											}
										>
											<i className={`fas fa-${isFullScreenModal ? 'compress-alt' : 'expand-alt'}`} />
										</Button>
									</IonRow>
								</IonRow>
							)}
						</IonCol>
					</IonRow>
					{this.renderTranscriptModal()}
				</IonGrid>
			</>
		)
	}
}

const FullscreenVideoPlayerInner = Container.default(FullScreenVideo)

// Functional wrapper so we can use the captions hook (the inner player is a
// class wrapped by the data Container). Only self-hosted, timecoded video
// yields a track URL; embeds and non-timecoded items get undefined.
const FullscreenVideoPlayer = (props: OwnProps): JSX.Element => {
	const { sectionItem } = props
	const captionTrackUrl = useTimecodedTrackUrl(sectionItem.id, sectionItem.transcription_format, getCurrentLanguage())
	return <FullscreenVideoPlayerInner {...(props as any)} captionTrackUrl={captionTrackUrl} />
}

export default FullscreenVideoPlayer as any
